/********************************************************************
 * ADOBE CONFIDENTIAL
 * __________________
 *
 *  Copyright 2025 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 *******************************************************************/

import type { FetchGraphQL } from '@adobe-commerce/fetch-graphql';
import { Initializer } from '@adobe-commerce/elsie/lib';
import { Lang } from '@adobe-commerce/elsie/i18n';
import { getCompanyHeaderManager } from '@/company-switcher/api/setCompanyHeaders/setCompanyHeaders';
import { getGroupHeaderManager } from '@/company-switcher/api/setGroupHeaders/setGroupHeaders';
import { getCustomerCompanyInfo } from '@/company-switcher/api/customerCompanyContext/customerCompanyContext';
import { events } from '@adobe-commerce/event-bus';

/**
 * Configuration properties for the company switcher initialization
 */
interface ConfigProps {
  /** Optional language definitions for internationalization */
  langDefinitions?: Lang;
  /** HTTP header name for company identification */
  companyHeader: string;
  /** HTTP header name for customer group identification */
  customerGroupHeader: string;
  /** Session storage key for persisting company context */
  companySessionStorageKey: string;
  /** Session storage key for persisting group context */
  groupSessionStorageKey: string;
  /** GraphQL modules that will have company headers applied */
  fetchGraphQlModules: FetchGraphQL[];
  /** GraphQL modules that will have group headers applied */
  groupGraphQlModules: FetchGraphQL[];
}

/**
 * Initializer for the company switcher drop-in.
 * Configures headers, storage, and GraphQL modules for company management.
 */
export const initialize = new Initializer<ConfigProps>({
  init: async (config) => {
    const defaultConfig = {
      fetchGraphQlModules: [],
      groupGraphQlModules: [],
      companyHeader: 'X-Adobe-Company',
      customerGroupHeader: 'Magento-Customer-Group',
      companySessionStorageKey: 'DROPIN__COMPANYSWITCHER__COMPANY__CONTEXT',
      groupSessionStorageKey: 'DROPIN__COMPANYSWITCHER__GROUP__CONTEXT'
    };
    initialize.config.setConfig({ ...defaultConfig, ...config });

    const groupId = sessionStorage.getItem(initialize.config.getConfig().groupSessionStorageKey);
    const companyId = sessionStorage.getItem(initialize.config.getConfig().companySessionStorageKey);
    if (companyId && !getCompanyHeaderManager().isCompanyHeaderSet()) {
      getCompanyHeaderManager().setCompanyHeaders(companyId);
      events.emit('companyContext/changed', companyId);
    }
    if (groupId && !getGroupHeaderManager().isGroupHeaderSet()) {
      getGroupHeaderManager().setGroupHeaders(groupId);
    }

    events.on('authenticated', async (authenticated: boolean) => {
      const config = initialize.config.getConfig();
      if (!authenticated) {
        sessionStorage.removeItem(config.companySessionStorageKey);
        sessionStorage.removeItem(config.groupSessionStorageKey);
        getCompanyHeaderManager().removeCompanyHeaders();
        getGroupHeaderManager().removeGroupHeaders();
        events.emit('companyContext/changed', null);
      } else {
        const storedCompanyId = sessionStorage.getItem(config.companySessionStorageKey);
        if (storedCompanyId || getCompanyHeaderManager().isCompanyHeaderSet()) {
          return;
        }
        const companyInfo = await getCustomerCompanyInfo();
        if (companyInfo.customerCompanies.length < 2) {
          return;
        }

        const currentCompanyId = companyInfo.currentCompany.id;
        const groupId = companyInfo.customerGroupId;
        getCompanyHeaderManager().setCompanyHeaders(currentCompanyId);
        getGroupHeaderManager().setGroupHeaders(groupId);
        sessionStorage.setItem(config.companySessionStorageKey, currentCompanyId);
        sessionStorage.setItem(config.groupSessionStorageKey, groupId);
        events.emit('companyContext/changed', currentCompanyId);
      }
    }, { eager: true });
  },
  listeners: () => []
});

/**
 * Configuration object for getting and setting company switcher configuration.
 * Provides methods to access and update the current configuration.
 */
export const config = initialize.config;
